import React, { useRef, useState } from "react";
import { Button, colors, H5, LabeledCheckbox, Popup } from "@commonsku/styles";
import { BASE_ZINDEX } from "../../../popup-factory";
import { PopupHeaderWithCloseIcon } from "../../FormPopup";
import { useContainerScroll } from "../../../hooks";
import { ScrollContainer } from "../../../hooks/useContainerScroll";
import { oauth } from "../../../utils";
import styled from "styled-components";

type ShopsFeeTermsAndConditionsPopupProps = {
  company_id: string;
  new_shop_user: boolean;
  disabled: boolean;
  on_close?: () => void;
  handle_custom_routing?: (
    route: string,
    selectedShopType?: string,
    shopName?: string,
    clientId?: string
  ) => void;
  set_is_shops_terms_and_conditions_accepted?: React.Dispatch<React.SetStateAction<boolean>>;
};

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: end;
  gap: 10px;
`;

const StyledListItem = styled.li`
  margin-bottom: 8px !important;
`;

const ShopsFeeTermsAndConditionsPopup = ({
  company_id,
  new_shop_user,
  disabled,
  on_close,
  handle_custom_routing,
  set_is_shops_terms_and_conditions_accepted,
}: ShopsFeeTermsAndConditionsPopupProps) => {
  const [checked, setChecked] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { canScrollDown, scrollDown } = useContainerScroll(contentRef);

  return (
    <Popup
      overlayZIndex={BASE_ZINDEX + 100}
      zIndex={BASE_ZINDEX + 200}
      style={{ width: 720, height: 728, borderRadius: 10 }}
      popupContentStyle={{ width: "100%" }}
      header={
        <PopupHeaderWithCloseIcon
          title={
            new_shop_user
              ? "Opt-In to the Shops Fees Model"
              : "Opt-In to the New Shops Fees Model"
          }
          onPopupClose={() => {
            if (on_close) {
              on_close();
            } else if (handle_custom_routing) {
              handle_custom_routing("initialize", null);
            }
          }}
        />
      }
      noCloseButton
    >
      <StyledContainer>
        <div
          ref={contentRef}
          style={{
            paddingLeft: "3px",
            paddingRight: "16px",
            overflowX: "hidden",
            overflowY: "auto",
            flex: 1,
            position: "relative",
            marginTop: 24,
          }}
        >
          <H5>Fee Structure</H5>
          <ul style={{ paddingLeft: "20px" }}>
            <StyledListItem>
              Popup, Company & Marketing Shops:
              <ul>
                <li>
                  2.5% on the first $25K → 2% on $25K–$50K → 1.5% on $50K–$100K
                  → 1% on $100K+ revenue.
                </li>
              </ul>
            </StyledListItem>

            <StyledListItem>
              Shopify Shops: 1% fee on correctly mapped orders after the grace
              period.
            </StyledListItem>

            <StyledListItem>
              The Shopify integration is only available for Advanced and
              Enterprise users.
            </StyledListItem>
          </ul>

          <H5 style={{ marginTop: "20px" }}>
            Here’s how shop fees work depending on your shop type:
          </H5>

          <ul style={{ paddingLeft: "20px" }}>
            <StyledListItem>
              Popup Shops: 7-day grace period before fees are locked
              (immediately if orders are exported in a CSV or converted to a
              Sales Order).
            </StyledListItem>
            <StyledListItem>
              Company Shops: Fees apply right away once an order is placed and
              project is created.
            </StyledListItem>
            <StyledListItem>
              Marketing Shops: Fees apply once the order is assigned to a client
              and project.
            </StyledListItem>
            <StyledListItem>
              Shopify Shops: 7-day grace period for correctly mapped orders;
              errors need to be fixed first.
            </StyledListItem>
          </ul>

          <p>
            The 7-day grace period gives you time to adjust test orders or
            customer changes before fees apply.
          </p>

          <p>
            Only admins can click 'Agree and Continue'. If you are not an admin,
            please speak to your admin to activate this feature.
          </p>

          {contentRef.current && (
            <ScrollContainer
              isUp={false}
              onClick={scrollDown}
              canScroll={canScrollDown()}
              color={colors.teal.main}
              width={contentRef.current.clientWidth - 10}
              zIndex={BASE_ZINDEX + 300}
              top={null}
              left={null}
              right={null}
              bottom={-25}
              style={{ position: "sticky", cursor: "pointer" }}
              margin={0}
              padding={10}
              arrowSize="large"
            />
          )}
        </div>

        <LabeledCheckbox
          label="I have read and agree to the Shops Fees Model Terms and Conditions."
          disabled={disabled}
          checked={checked}
          onChange={() => setChecked(!checked)}
        />

        <ButtonContainer>
          <Button
            variant="secondary"
            size="medium"
            onClick={() => {
              if (on_close) {
                on_close();
              } else if (handle_custom_routing) {
                handle_custom_routing("initialize", null);
              }
            }}
          >
            Cancel
          </Button>

          <Button
            disabled={!checked || disabled}
            variant="primary"
            size="medium"
            onClick={() => {
              if (disabled) {
                return;
              }

              (new_shop_user
                ? oauth("PUT", `tenant/${company_id}`, {
                    accept_shop_charges: 1,
                  }).then(() =>
                    oauth("GET", "company/accept-shops-terms-and-conditions", {
                      accept: 1,
                    })
                  )
                : oauth("GET", "company/accept-shops-terms-and-conditions", {
                    accept: 1,
                  })
              )
                .then(() => {
                  localStorage.removeItem(`shops.fees_popup_${company_id}`);
                  if (set_is_shops_terms_and_conditions_accepted && handle_custom_routing) {
                    set_is_shops_terms_and_conditions_accepted(true);
                    handle_custom_routing("selectedShopPage", "SHOPIFY");
                  } else {
                    window.location.reload();
                  }
                })
                .catch((err) => {
                  // Handle error appropriately
                })
            }}
          >
            Agree and Continue
          </Button>
        </ButtonContainer>
      </StyledContainer>
    </Popup>
  );
};

export default ShopsFeeTermsAndConditionsPopup;
